import { useState, useEffect } from "react";
import { Loader2, AlertCircle, Ban } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { projectsApi } from "@/lib/api/projects";
import { deploymentsApi } from "@/lib/api/deployments";
import { useProjectStore } from "@/stores/projectStore";
import toast from "react-hot-toast";

interface RenameProjectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  currentName: string;
  onSuccess?: (newName: string, newUrlSlug: string) => void;
}

export function RenameProjectModal({
  open,
  onOpenChange,
  projectId,
  currentName,
  onSuccess,
}: RenameProjectModalProps) {
  const [newName, setNewName] = useState(currentName);
  const [renameLoading, setRenameLoading] = useState(false);
  const [hasBeenPublished, setHasBeenPublished] = useState(false);
  const [checkingPublishStatus, setCheckingPublishStatus] = useState(false);
  const { getIdToken } = useAuth();
  const { updateProject: updateProjectStore, incrementProjectListVersion, isPublishing } = useProjectStore();

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setNewName(currentName);
      setCheckingPublishStatus(true);

      // Check if project has been published
      const checkPublishStatus = async () => {
        try {
          const currentDeploy = await deploymentsApi.getCurrentDeployment(projectId);
          setHasBeenPublished(!!currentDeploy);
        } catch {
          setHasBeenPublished(false);
        } finally {
          setCheckingPublishStatus(false);
        }
      };
      checkPublishStatus();
    }
  }, [open, currentName, projectId]);

  const handleRename = async () => {
    if (!newName.trim() || newName === currentName) return;

    try {
      setRenameLoading(true);
      const token = await getIdToken();
      if (!token) {
        throw new Error("Authentication required");
      }

      const result = await projectsApi.rename(token, projectId, newName.trim());

      // Update global store with all URL-related fields (fixes stale preview URLs after rename)
      updateProjectStore({
        name: result.project.name,
        urlSlug: result.project.urlSlug,
        latestPreviewUrl: result.project.latestPreviewUrl,
        latestPublishUrl: result.project.latestPublishUrl,
        latestPreviewBuildNumber: result.project.latestPreviewBuildNumber,
        latestPreviewBuildId: result.project.latestPreviewBuildId,
      });

      // Trigger project list refresh (for Dashboard thumbnails)
      incrementProjectListVersion();

      onOpenChange(false);
      onSuccess?.(result.project.name, result.project.urlSlug);
      toast.success("Project renamed successfully!");

      // Show warning if Supabase site_url update failed
      if (result.warning) {
        toast.error(result.warning, { duration: 6000 });
      }
    } catch (err: unknown) {
      console.error("Failed to rename project:", err);
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      const errorMessage =
        error.response?.data?.message || error.message || "Failed to rename project";
      toast.error(errorMessage);
    } finally {
      setRenameLoading(false);
    }
  };

  // Prevent closing while rename is in progress
  const handleOpenChange = (newOpen: boolean) => {
    if (renameLoading) return; // Don't allow closing while renaming
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Rename Project</DialogTitle>
          <DialogDescription>
            Change your project name and URL slug.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">New Name</label>
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Enter new project name"
              disabled={renameLoading || checkingPublishStatus || isPublishing}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !renameLoading && !checkingPublishStatus && !isPublishing && newName.trim() && newName !== currentName) {
                  handleRename();
                }
              }}
            />
          </div>

          {isPublishing ? (
            <div className="flex items-start gap-2 p-3 rounded-md border border-blue-500/50 bg-blue-500/10">
              <Ban className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-muted-foreground">
                Cannot rename while publishing. Please wait for the publish to complete.
              </p>
            </div>
          ) : checkingPublishStatus ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Checking publish status...
            </div>
          ) : hasBeenPublished && (
            <div className="flex items-start gap-2 p-3 rounded-md border border-amber-500/50 bg-amber-500/10">
              <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-muted-foreground">
                This project has been published. The public URL will change to match the new name.
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={renameLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRename}
            disabled={renameLoading || checkingPublishStatus || isPublishing || !newName.trim() || newName === currentName}
          >
            {renameLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Renaming...
              </>
            ) : (
              "Rename"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
